import classNames from 'classnames';
import { Dialog } from 'rc-ui-lib';
import type { DialogProps } from 'rc-ui-lib/lib/dialog/PropsType';
import { noop } from 'rc-ui-lib/lib/utils';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isEn } from '../../utils/util';
import style from './index.module.less';
import { isOverTwoLines, shouldUseVerticalLayout } from './utils';

export type CommonDialogProps = {
  title?: string;
  content?: string | React.ReactNode;
  btnList?: Array<{
    text: string;
    isPrimary?: boolean;
    onClick: () => void;
  }>;
  contentClsName?: string;
  showBottomCover?: boolean;
} & DialogProps;

type ClickFunc = DialogProps['onConfirm'] | DialogProps['onCancel'];

function CommonDialog(props: CommonDialogProps) {
  const {
    title,
    content,
    contentClsName,
    showBottomCover,
    visible,
    cancelButtonText,
    confirmButtonText,
    btnList,
    onConfirm,
    onCancel,
    showConfirmButton,
    ...restProps
  } = props;
  const [isContentOverTwoLines, setContentOverTwoLines] = useState(false);
  const [isContentScrollable, setIsContentScrollable] = useState(false);
  const [internalVisible, setInternalVisible] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const retryRafIdRef = useRef<number | null>(null);

  const isEng = isEn();

  /**
   * 在显示前先计算内容状态，避免样式闪烁
   * 当外部 visible 变为 true 时，先渲染隐藏的测量容器进行计算
   * 计算完成后，再设置 internalVisible 为 true 显示 Dialog
   * 如果是英文环境，不需要计算是否多行，但仍需要计算是否滚动
   
   */
  useLayoutEffect(() => {
    if (!visible) {
      // 外部 visible 变为 false，立即关闭
      setInternalVisible(false);
      return;
    }

    // 外部 visible 变为 true，重置状态并开始计算
    setInternalVisible(false);
    setContentOverTwoLines(false);
    setIsContentScrollable(false);
    retryRafIdRef.current = null;

    /**
     * 统一的计算函数，用于测量内容状态
     * 英文环境下不计算多行，但仍需要计算滚动状态
     
     */
    const calculateContentState = () => {
      if (!measureRef.current) {
        return false;
      }

      const element = measureRef.current;
      // 检查元素是否有内容
      if (element.scrollHeight > 0 || element.children.length > 0) {
        // 英文环境下不计算多行，非英文环境才计算
        if (!isEng) {
          setContentOverTwoLines(isOverTwoLines(element as HTMLElement));
        }
        // 所有环境都需要计算滚动状态
        setIsContentScrollable(element.scrollHeight > element.clientHeight);
        // 计算完成后显示 Dialog
        setInternalVisible(true);
        return true;
      }
      return false;
    };

    // 使用 requestAnimationFrame 确保测量容器已经渲染完成
    const rafId = requestAnimationFrame(() => {
      if (!calculateContentState()) {
        // 如果元素还没有内容，延迟一帧再试
        retryRafIdRef.current = requestAnimationFrame(() => {
          calculateContentState();
        });
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (retryRafIdRef.current !== null) {
        cancelAnimationFrame(retryRafIdRef.current);
      }
    };
  }, [visible, content, isEng]);

  const hasTitle = useMemo(() => !!title, [title]);
  const hasContent = useMemo(() => !!content, [content]);

  /**
   * 判断是否需要使用竖状排列
   
   */
  const isBtnVertical = useMemo(
    () => shouldUseVerticalLayout(btnList, confirmButtonText, cancelButtonText),
    [btnList, confirmButtonText, cancelButtonText]
  );

  /**
   * 构建竖状排列的 footer
   
   */
  const verticalFooter = useMemo(() => {
    if (!isBtnVertical) {
      return undefined;
    }

    // 统一构建按钮列表
    const buttons: Array<{
      text: string;
      isPrimary?: boolean;
      onClick: ClickFunc;
      key: string;
    }> = [];

    // 如果传入了 btnList，使用 btnList 构建按钮
    if (btnList && btnList.length > 0) {
      btnList.forEach((btn, index) => {
        buttons.push({
          text: btn.text,
          isPrimary: btn.isPrimary,
          onClick: btn.onClick,
          key: `${btn.text}-${index}` || `btn-${index}`,
        });
      });
    } else {
      // 否则使用 confirmButtonText 和 cancelButtonText 构建按钮
      if (confirmButtonText) {
        buttons.push({
          text: confirmButtonText,
          isPrimary: true,
          onClick: onConfirm || noop,
          key: 'confirm',
        });
      }
      if (cancelButtonText) {
        buttons.push({
          text: cancelButtonText,
          isPrimary: false,
          onClick: onCancel || noop,
          key: 'cancel',
        });
      }
    }

    // 统一渲染按钮列表
    if (buttons.length === 0) {
      return undefined;
    }

    return (
      <div className={style.vertical_footer}>
        {buttons.map(({ key, text, isPrimary, onClick }) => (
          <div key={key} className={style.vertical_footer_item} onClick={onClick}>
            <div
              className={classNames(style.vertical_footer_btn, {
                [style.vertical_footer_btn_primary]: isPrimary,
              })}
            >
              {text}
            </div>
          </div>
        ))}
      </div>
    );
  }, [isBtnVertical, btnList, cancelButtonText, confirmButtonText, onCancel, onConfirm]);

  return (
    <>
      {/* 隐藏的测量容器，用于在显示前计算内容状态 */}
      {visible && !internalVisible && (
        <div
          ref={measureRef}
          style={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            top: '-9999px',
            left: '-9999px',
            width: '295px', // Dialog 的宽度
          }}
          className={classNames(style.content, contentClsName, {
            [style.no_title]: !hasTitle,
            [style.no_content]: !hasContent,
          })}
        >
          {content}
        </div>
      )}
      <Dialog
        {...restProps}
        title={title}
        visible={internalVisible}
        showCancelButton={!isBtnVertical && !!cancelButtonText}
        showConfirmButton={
          typeof showConfirmButton !== 'undefined'
            ? showConfirmButton
            : !isBtnVertical && !!confirmButtonText
        }
        cancelButtonText={!isBtnVertical ? cancelButtonText : undefined}
        confirmButtonText={!isBtnVertical ? confirmButtonText : undefined}
        onConfirm={!isBtnVertical ? onConfirm : undefined}
        onCancel={!isBtnVertical ? onCancel : undefined}
        footer={isBtnVertical ? verticalFooter : undefined}
        className={style.dialog}
      >
        <div
          className={classNames(style.content_wrapper, {
            // [style.content_wrapper_with_gradient]: true,
          })}
        >
          <div
            ref={contentRef}
            className={classNames(style.content, contentClsName, {
              [style.content_center]: isEng || !isContentOverTwoLines,
              [style.no_title]: !hasTitle,
              [style.no_content]: !hasContent,
              [style.content_scrollable]: isContentScrollable,
            })}
          >
            {content}
          </div>
          {/* byClaude-start */}
          {/* 仅在内容可滚动时渲染渐变遮罩，避免遮挡无标题弹窗的分割线 */}
          {isContentScrollable && <div className={style.content_wrapper_with_gradient_after} />}
          {/* byClaude-end */}
        </div>
      </Dialog>
    </>
  );
}

export default CommonDialog;
